import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { getPortalAttendanceSummary } from "../../lib/api";

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const CODE_STYLE = {
  P: "bg-emerald-100 text-emerald-800",
  PT: "bg-emerald-100 text-emerald-800",
  T: "bg-amber-100 text-amber-800",
  A: "bg-red-100 text-red-700",
  UA: "bg-red-100 text-red-700",
  EA: "bg-blue-100 text-blue-700",
};
const codeStyle = (c) => CODE_STYLE[c] || "bg-slate-100 text-slate-400";

export default function PortalAttendance() {
  const today = new Date();
  const [start, setStart] = useState(isoDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [end, setEnd] = useState(isoDate(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPortalAttendanceSummary(start, end);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load attendance");
    } finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const students = data?.students || [];
  const dates = data?.dates || [];

  return (
    <div className="space-y-4" data-testid="portal-attendance">
      <Card>
        <CardContent className="py-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">From</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" data-testid="portal-att-start" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">To</label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" data-testid="portal-att-end" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="portal-att-run">
            {loading ? "Loading..." : "View"}
          </Button>
          <div className="flex-1" />
          <div className="text-xs text-muted-foreground">
            <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 mr-1">P</span>Present
            <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mx-1">T</span>Tardy
            <span className="inline-block px-1.5 py-0.5 rounded bg-red-100 text-red-700 mx-1">A</span>Absent
            <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 mx-1">EA</span>Excused
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Loading attendance…</CardContent></Card>
      ) : students.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="portal-att-empty">No students found for this district in the selected range.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="portal-att-table">
              <thead>
                <tr className="border-b bg-slate-50 text-left">
                  <th className="p-3 sticky left-0 bg-slate-50 z-10">Student</th>
                  <th className="p-3 text-center">Rate</th>
                  <th className="p-3 text-center">P</th>
                  <th className="p-3 text-center">T</th>
                  <th className="p-3 text-center">A</th>
                  {dates.map((d) => (
                    <th key={d} className="p-1 text-center text-[10px] font-medium text-muted-foreground whitespace-nowrap">{d.slice(5)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.student_id} className="border-b hover:bg-slate-50" data-testid={`portal-att-row-${s.student_id}`}>
                    <td className="p-3 font-medium sticky left-0 bg-white z-10 whitespace-nowrap">{s.student_name}</td>
                    <td className="p-3 text-center font-semibold">{s.summary?.attendance_rate == null ? '—' : `${s.summary.attendance_rate}%`}</td>
                    <td className="p-3 text-center">{s.summary?.present ?? 0}</td>
                    <td className="p-3 text-center">{s.summary?.tardy ?? 0}</td>
                    <td className="p-3 text-center">{s.summary?.absent ?? 0}</td>
                    {dates.map((d) => {
                      const cell = s.daily_attendance?.[d] || {};
                      const code = cell.code || "";
                      return (
                        <td key={d} className="p-1 text-center">
                          <span className={`inline-block w-6 text-[10px] rounded ${codeStyle(code)}`}>{code === "-" ? "" : code}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
